import "./Settings.css";
import { Fragment, useState } from "react";
import { Edit, Lock } from "react-feather";
import { Link, Outlet } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { Switch } from "@mui/material";
import { ThemeProvider as MUIThemeProvider } from "@mui/material/styles";
import { Content, Header, Main } from "../../commons/Template/Template";
import { Form } from "../../commons/Form/Form";
import Pill from "../../Financing/Common/Pill/Pill";
import authAPI from "../../../api/authentication";
import routes from "../../../routes/routes";
import { switchTheme } from "../../commons/Switch/Switch";
import { useAuth } from "../../../auth/AuthContext";
import { useModalDispatch } from "../../commons/Modal/ModalContext";
import useRootRedirect from "../../../hooks/commons/useRootRedirect";
import Tabs from "../../commons/Tabs/Tabs";
import Typo from "../../commons/Typo/Typo";

export default function Settings() {
  const { t } = useTranslation(["common", "navigation", "settings"]);
  const { user, activeCompany } = useAuth();

  const tabKey = "settings";

  const keys = [
    "profile",
    "password",
    "users",
    "company",
    "subscription",
    "theme",
  ];

  const hidden = (key) => {
    const map = {
      users: !user?.is_admin || !activeCompany,
      company: !activeCompany,
      subscription: !activeCompany,
      theme: !activeCompany?.is_reseller,
    };

    return map[key] || false;
  };

  const tabs = keys.map((key) => ({
    to: routes.Settings[key],
    title: t(`settings:tabs.${key}.title`),
    hidden: hidden(key),
  }));

  useRootRedirect(routes.Settings.root, routes.Settings.profile);

  return (
    <Main themeSwitch>
      <Header>
        <Typo variant="h1">{t(`navigation:tabs.${tabKey}.title`)}</Typo>
        <Typo variant="body2">{t(`navigation:tabs.${tabKey}.subheading`)}</Typo>
      </Header>
      <Tabs tabs={tabs} />
      <Content className="Settings">
        <Outlet />
        <InfoShare />
      </Content>
    </Main>
  );
}

function InfoShare() {
  const { t } = useTranslation(["common", "settings"]);

  return (
    <Info
      className="InfoShare"
      title={t("settings:info.share.title")}
      text={t("settings:info.share.text")}
    />
  );
}

function Info({ className = "", title = "", text = "" }) {
  return (
    <aside className={"Info " + className}>
      <strong className="Info-title">{title}</strong>
      <Typo className="Info-text">{text}</Typo>
    </aside>
  );
}

export function SettingList({ children }) {
  return <div className="SettingList">{children}</div>;
}

export function Setting({ testid, children }) {
  return (
    <div className="Setting" data-testid={"setting-" + testid}>
      {children}
    </div>
  );
}

export function SettingDisplay({ label, value, tag, tagColor, testid }) {
  return (
    <div className="SettingDisplay">
      <div className="SettingDisplay-label">
        <span>{label}</span>
        {tag && <Pill text={tag} color={tagColor} />}
      </div>
      <div className="SettingDisplay-value">
        <span data-testid={testid}>{value}</span>
      </div>
    </div>
  );
}

function SettingChanger({ className, icon, action, onClick, children }) {
  const { t } = useTranslation(["common"]);
  return (
    <div
      onClick={onClick}
      className={["SettingChanger", className].filter(Boolean).join(" ")}
    >
      {children ? (
        children
      ) : (
        <Fragment>
          <div>{icon}</div> {action && <span>{t(`common:${action}`)}</span>}
        </Fragment>
      )}
    </div>
  );
}

export function SettingLocker() {
  const lockedDataModal = useLockedDataModal();
  const dispatchModal = useModalDispatch();

  return (
    <SettingChanger
      icon={<Lock />}
      className="Locker"
      onClick={() => dispatchModal({ type: "add", ...lockedDataModal })}
    />
  );
}

export function SettingSwitcher({ value, onSwitch }) {
  const handleSwitchChange = ({ target: { checked } }) => onSwitch(checked);

  return (
    <SettingChanger className={"Switcher"}>
      <MUIThemeProvider theme={switchTheme}>
        <Switch
          size="small"
          checked={value}
          onChange={(newValue) => handleSwitchChange(newValue)}
          inputProps={{ "aria-label": "controlled" }}
        />
      </MUIThemeProvider>
    </SettingChanger>
  );
}

export function SettingModder({
  icon = <Edit />,
  action = "modify",
  modal,
  onClick,
}) {
  const dispatchModal = useModalDispatch();
  const { t } = useTranslation(["common"]);
  return (
    <SettingChanger
      className={"Modder"}
      icon={icon}
      action={action}
      onClick={
        modal
          ? () =>
              dispatchModal({
                type: "add",
                title: t("common:ModifyTitle"),
                ...modal,
              })
          : () => onClick()
      }
    />
  );
}

function useLockedDataModal() {
  const { t } = useTranslation(["common", "settings"]);

  const modal = {
    title: t("settings:modals.lockedData.title"),
    message: (
      <Trans
        t={t}
        i18nKey="settings:modals.lockedData.message"
        components={{
          anchor: <Link to={`mailto:${routes.EmailSupport}`} />,
        }}
      />
    ),
  };

  return modal;
}

export function VerifyPasswordModal({ onNextModal = null }) {
  const dispatchModal = useModalDispatch();
  const { t } = useTranslation(["common", "settings"]);
  const [password, setPassword] = useState("");
  const [errorsObj, setErrorsObj] = useState({});

  const handlePasswordCheck = async () => {
    setErrorsObj({});

    const response = await authAPI.reauthenticate(password);
    if (response.ok) {
      setPassword("");
      if (onNextModal) {
        onNextModal();
      } else {
        dispatchModal({ type: "success" });
      }
    } else {
      setErrorsObj({
        password: [t("settings:modals.verifyPassword.component.form.error")],
      });
    }
  };

  const getErrors = (property) =>
    errorsObj.hasOwnProperty(property) ? errorsObj[property] : [];

  const passwordInput = [
    {
      label: t("common:password"),
      name: "current-password",
      autoComplete: "current-password",
      type: "password",
      value: password,
      onChange: setPassword,
      errors: getErrors("password"),
    },
  ];

  return (
    <Form
      onSubmit={handlePasswordCheck}
      inputs={[{ data: passwordInput }]}
      errors={getErrors("non_field_errors")}
    />
  );
}
